import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Edit2, Download, Search, FileText, Printer, Truck, ArrowDownToLine } from 'lucide-react';
import { primaryButton, ghostButton, inputStyle, glass, glassLight, T, Badge, StatCard, EmptyState, Field, usePagination, Pagination, useDesplazarAlAbrir } from '../ui';
import { LOGO_DATA_URI } from '../Logo';
// El tag "style" se arma por partes (LT + 'style' ...). Si se escribe literal
// el tag de apertura de style dentro de un template literal, el formateador de
// codigo lo interpreta como JSX y corrompe el archivo (envuelve el CSS en
// llaves y rompe el string). Construirlo con concatenacion evita ese patron.
const LT = '<';
const styleTag = (css: string) => `${LT}style>${css}${LT}/style>`;
interface Cliente {
  id: string;
  codigo: string;
  nombre: string;
  telefono: string;
  direccion: string;
  capacidadTanque: number;
  rnc?: string;
  representante?: string;
  representanteCedula?: string;
  representanteDireccion?: string;
}
interface Tanque {
  capacidad: number;
  valor: number;
}
interface Contrato {
  id: string;
  numeroContrato: string;
  clienteId: string;
  clienteNombre: string;
  fechaInicio: string;
  fechaVencimiento: string;
  precioAcordado: number;
  condiciones: string;
  estado: 'vigente' | 'vencido' | 'cancelado';
  firmaDigital?: string;
  fecha_creacion: string;
  // Campos legacy (mantenidos por compatibilidad)
  cantidadTanques?: number;
  capacidadGalones?: number;
  valorTanque?: number;
  // Nuevo campo para múltiples tanques con diferentes capacidades
  tanques?: Tanque[];
  ciudad?: string;
}
interface ContratosProps {
  contratos: Contrato[];
  clientes: Cliente[];
  onAddContrato: (contrato: Contrato) => void;
  onDeleteContrato: (id: string) => void;
  onUpdateContrato: (contrato: Contrato) => void;
}
const estadoStyle = (estado: Contrato['estado']) => {
  if (estado === 'vigente') return {
    color: T.ok,
    soft: T.okSoft
  };
  if (estado === 'vencido') return {
    color: T.danger,
    soft: T.dangerSoft
  };
  return {
    color: T.textMuted,
    soft: T.surfaceSubtle
  };
};
const iconBtn: React.CSSProperties = {
  width: 30,
  height: 30,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  border: `1px solid ${T.border}`,
  background: T.surface,
  cursor: 'pointer'
};
// Abre una ventana nueva y escribe el documento HTML ya armado.
function openPrintWindow(html: string, autoPrint: boolean) {
  const w = window.open('', '_blank');
  if (!w) {
    alert('El navegador bloqueó la ventana. Habilita las ventanas emergentes.');
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();
  if (autoPrint) setTimeout(() => w.print(), 300);
}
export function Contratos({
  contratos,
  clientes,
  onAddContrato,
  onDeleteContrato,
  onUpdateContrato
}: ContratosProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  // Al editar un registro del final del listado, el formulario se abre arriba:
  // sin llevar la vista hasta él parece que el botón no hizo nada.
  const refForm = useDesplazarAlAbrir(showForm, editingId);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState<'todos' | 'vigente' | 'vencido' | 'cancelado'>('vigente');
  const defaultTanques = [{
    capacidad: 120,
    valor: 140000
  }];
  const [formData, setFormData] = useState<Partial<Contrato>>({
    estado: 'vigente',
    precioAcordado: 0,
    ciudad: 'Santiago de los Caballeros',
    tanques: defaultTanques
  });
  const [selectedCliente, setSelectedCliente] = useState<string>('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.numeroContrato || !selectedCliente || !formData.fechaInicio || !formData.fechaVencimiento) {
      alert('Por favor completa los campos obligatorios');
      return;
    }
    const cliente = clientes.find(c => c.id === selectedCliente);
    // Sincronizar campos legacy para compatibilidad
    const tanques = formData.tanques || defaultTanques;
    const cantidadTanques = tanques.length;
    const capacidadGalones = tanques[0]?.capacidad || 0;
    const valorTanque = tanques[0]?.valor || 0;
    const contratoData = {
      ...formData,
      clienteId: selectedCliente,
      clienteNombre: cliente?.nombre || '',
      tanques,
      cantidadTanques,
      capacidadGalones,
      valorTanque
    };
    if (editingId) {
      onUpdateContrato({
        ...contratoData,
        id: editingId
      } as Contrato);
      setEditingId(null);
    } else {
      onAddContrato({
        ...contratoData,
        id: Date.now().toString(),
        fecha_creacion: new Date().toISOString().split('T')[0]
      } as Contrato);
    }
    setFormData({
      estado: 'vigente',
      precioAcordado: 0,
      ciudad: 'Santiago de los Caballeros',
      tanques: [{
        capacidad: 120,
        valor: 140000
      }]
    });
    setSelectedCliente('');
    setShowForm(false);
  };
  const handleAddTanque = () => {
    const currentTanques = formData.tanques || [];
    setFormData({
      ...formData,
      tanques: [...currentTanques, {
        capacidad: 120,
        valor: 140000
      }]
    });
  };
  const handleRemoveTanque = (index: number) => {
    const currentTanques = formData.tanques || [];
    if (currentTanques.length <= 1) return;
    const newTanques = [...currentTanques];
    newTanques.splice(index, 1);
    setFormData({
      ...formData,
      tanques: newTanques
    });
  };
  const handleTanqueChange = (index: number, field: 'capacidad' | 'valor', value: number) => {
    const currentTanques = formData.tanques || [];
    const newTanques = [...currentTanques];
    newTanques[index] = {
      ...newTanques[index],
      [field]: value
    };
    setFormData({
      ...formData,
      tanques: newTanques
    });
  };
  const numeroALetras = (n: number) => {
    const map: Record<number, string> = {
      1: 'uno (1)',
      2: 'dos (2)',
      3: 'tres (3)',
      4: 'cuatro (4)',
      5: 'cinco (5)',
      6: 'seis (6)'
    };
    return map[n] || `${n} (${n})`;
  };
  const getTanquesList = (contrato: Contrato, cliente: Cliente) => {
    if (contrato.tanques && contrato.tanques.length > 0) {
      return contrato.tanques;
    }
    // Fallback legacy
    const cant = contrato.cantidadTanques || 1;
    const cap = contrato.capacidadGalones || cliente.capacidadTanque || 0;
    const val = contrato.valorTanque || 0;
    return Array(cant).fill({
      capacidad: cap,
      valor: val
    });
  };
  const imprimirContrato = (contrato: Contrato) => {
    const cliente = clientes.find(c => c.id === contrato.clienteId);
    if (!cliente) {
      alert('No se encontró el cliente de este contrato.');
      return;
    }
    const ciudad = contrato.ciudad || 'Santiago de los Caballeros';
    const tanques = getTanquesList(contrato, cliente);
    const cantidad = tanques.length;
    const fmtFecha = (iso: string) => {
      if (!iso) return '____________';
      const d = new Date(iso + 'T00:00:00');
      return d.toLocaleDateString('es-DO', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    };
    const fmtMoneda = (n: number) => new Intl.NumberFormat('es-DO', {
      minimumFractionDigits: 2
    }).format(n);
    const css = `
      @page { size: letter; margin: 2.2cm 2.4cm; }
      * { -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
      body { font-family: 'Georgia', 'Times New Roman', serif; font-size: 12.5pt; line-height: 1.5; color: #111; }
      h1 { text-align: center; font-size: 15pt; margin: 0 0 2px; letter-spacing: .02em; }
      h1.sub { margin-bottom: 22px; }
      p { text-align: justify; margin: 0 0 14px; }
      strong { font-weight: 700; }
      .clausula { margin-bottom: 14px; }
      .parrafo { margin: 6px 0 6px 18px; }
      .firmas { margin-top: 60px; display: flex; justify-content: space-between; gap: 40px; }
      .firma { flex: 1; text-align: center; }
      .firma .linea { border-top: 1px solid #111; margin-top: 50px; padding-top: 6px; font-size: 11pt; }
      .meta { text-align: right; font-size: 11pt; margin-bottom: 24px; }
      .letterhead { display: flex; align-items: center; gap: 12px; justify-content: center; margin-bottom: 10px; }
      .letterhead img { height: 46px; width: auto; }
      ul.tank-list { margin: 6px 0 6px 18px; padding-left: 20px; }
      ul.tank-list li { margin-bottom: 4px; }
      @media print { .no-print { display: none; } }
      .no-print { position: fixed; top: 14px; right: 14px; }
      .no-print button { background: #FF6B01; color: #fff; border: none; padding: 10px 18px; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: sans-serif; }
    `;
    let tankDetails = '';
    if (cantidad === 1) {
      const t = tanques[0];
      tankDetails = `un cilindro${t.capacidad ? ` con capacidad para ${t.capacidad} galones` : ''}${t.valor ? ` valorado en RD$ ${fmtMoneda(t.valor)} pesos` : ''}`;
    } else {
      tankDetails = `${numeroALetras(cantidad)} cilindros con las siguientes especificaciones:
      <ul class="tank-list">
        ${tanques.map(t => `<li>Un cilindro${t.capacidad ? ` de ${t.capacidad} galones` : ''}${t.valor ? ` valorado en RD$ ${fmtMoneda(t.valor)} pesos` : ''}.</li>`).join('')}
      </ul>`;
    }
    const head = `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>Contrato ${contrato.numeroContrato}</title>
${styleTag(css)}
</head>`;
    const body = `
<body>
  <div class="no-print"><button onclick="window.print()">Imprimir / Guardar PDF</button></div>

  <div class="letterhead"><img src="${LOGO_DATA_URI}" alt="Junquito Gas" /></div>
  <div class="meta">${ciudad}, Rep. Dom., ${fmtFecha(contrato.fechaInicio)}</div>

  <h1>CONTRATO DE INSTALACIÓN DE CILINDROS</h1>
  <h1 class="sub">Y VENTA DE GLP INDUSTRIAL</h1>

  <p><strong>ENTRE:</strong> La compañía <strong>JUNQUITO GAS, SRL.</strong>, portadora del RNC
  1-30-16152-6, teléfono 809-247-2104, ubicada en la Av. Antonio Guzmán No. 102 de esta ciudad
  de Santiago de los Caballeros; quien en lo adelante y para fines y consecuencias legales del
  presente acto, se denominará <strong>LA EMPRESA</strong>.</p>

  <p><strong>Y DE LA OTRA PARTE</strong>, ${cliente.nombre}${cliente.rnc ? `, portador de RNC ${cliente.rnc}` : ''}${cliente.telefono ? `, teléfono ${cliente.telefono}` : ''}${cliente.direccion ? `, ubicado en ${cliente.direccion}` : ''}.${cliente.representante ? ` El mismo, propiedad del señor(a) <strong>${cliente.representante}</strong>, dominicano(a), mayor de edad${cliente.representanteCedula ? `, portador(a) de la cédula de identidad y electoral número ${cliente.representanteCedula}` : ''}${cliente.representanteDireccion ? `, domiciliado(a) en ${cliente.representanteDireccion}` : ''}.` : ''} En lo adelante y para fines y consecuencias legales del presente acto, se denominará
  <strong>EL CLIENTE</strong>.</p>

  <p style="text-align:center"><strong>SE HA CONVENIDO Y PACTADO LO SIGUIENTE:</strong></p>

  <div class="clausula"><p><strong>PRIMERO:</strong> LA EMPRESA instalará ${cantidad > 1 ? `${numeroALetras(cantidad)} cilindros` : 'un cilindro'} estacionario${cantidad > 1 ? 's' : ''} para el despacho de GLP, por pedido de EL CLIENTE, el cual debe realizarse con
  veinticuatro (24) horas de anticipación, para poder brindar un mejor servicio.</p></div>

  <div class="clausula"><p><strong>SEGUNDO:</strong> El/los cilindro(s) será(n) propiedad de LA EMPRESA,
  por lo que EL CLIENTE no podrá descargar de ningún otro proveedor. Para asegurar el
  cumplimiento de este requisito, LA EMPRESA contará con un sistema de seguridad que le
  permitirá monitorear las descargas.</p>
  <p class="parrafo"><strong>PÁRRAFO I:</strong> LA EMPRESA hace entrega de ${tankDetails}, este equipo será propiedad de LA EMPRESA. En caso de pérdida o deterioro EL CLIENTE debe
  pagarlo en su totalidad al precio del día en el mercado.</p>
  <p class="parrafo"><strong>PÁRRAFO II:</strong> Es responsabilidad de EL CLIENTE el cuidado y
  mantenimiento de todos los equipos instalados, así como el reemplazo de las piezas. El
  cilindro no podrá estar pegado a la tierra, sino que deberá tener una base que lo mantenga en
  buen estado; y cumplir con todos los requisitos de seguridad que exijan las autoridades
  nacionales y municipales.</p>
  <p class="parrafo"><strong>PÁRRAFO III:</strong> EL CLIENTE debe poseer un extintor de veinte
  (20) libras para ser usado en caso de emergencias. LA EMPRESA no es responsable ante
  cualquier accidente que pueda ocurrir por el mal manejo del equipo una vez instalado.</p></div>

  <div class="clausula"><p><strong>TERCERO:</strong> El precio de GLP se fijará conforme la
  RESOLUCIÓN SEMANAL del Ministerio de Industria y Comercio${contrato.precioAcordado ? ` (precio de referencia acordado: RD$ ${fmtMoneda(contrato.precioAcordado)})` : ''}.</p>
  <p class="parrafo"><strong>PÁRRAFO I:</strong> EL CLIENTE debe realizar el pago de la factura
  antes de hacer su próximo pedido.</p>
  <p class="parrafo"><strong>PÁRRAFO II:</strong> Las facturas no podrán exceder los treinta (30)
  días para ser pagadas, aunque EL CLIENTE no vaya a realizar un próximo pedido.</p></div>

  <div class="clausula"><p><strong>CUARTO:</strong> Este contrato tendrá vigencia a partir del día
  ${fmtFecha(contrato.fechaInicio)} y se vence de manera definitiva el día
  ${fmtFecha(contrato.fechaVencimiento)}.</p>
  <p class="parrafo"><strong>PÁRRAFO I:</strong> El presente contrato quedará automáticamente
  renovado por un nuevo período, en caso de que sesenta (60) días antes de la fecha de
  vencimiento ninguna de las partes notifique por escrito su deseo de no continuar.</p></div>

  <div class="clausula"><p><strong>QUINTO:</strong> En caso de que EL CLIENTE desee terminar el
  contrato antes de la fecha establecida, deberá comunicarlo por escrito a LA EMPRESA con
  quince (15) días de anticipación.</p></div>

  <div class="clausula"><p><strong>SEXTO:</strong> Las partes pactan que LA EMPRESA se reserva el
  derecho y la opción de renovar el presente contrato a la llegada de su término, situación que
  acepta y reconoce EL CLIENTE.</p></div>

  <div class="clausula"><p><strong>SÉPTIMO:</strong> Las partes hacen elección de domicilio en sus
  respectivas direcciones anteriormente descritas.</p></div>

  <div class="clausula"><p><strong>OCTAVO:</strong> LA EMPRESA se reserva el derecho de retirar
  los equipos instalados en el momento que lo considere necesario, sin incurrir en ninguna
  responsabilidad.</p></div>

  ${contrato.condiciones ? `<div class="clausula"><p><strong>CONDICIONES ADICIONALES:</strong> ${contrato.condiciones}</p></div>` : ''}

  <p>HECHO en dos originales de un mismo tenor y efecto, uno para cada una de las partes, en la
  ciudad de ${ciudad}, hoy ${fmtFecha(contrato.fechaInicio)}.</p>

  <div class="firmas">
    <div class="firma">
      <div class="linea">LA EMPRESA<br/>Junquito Gas, SRL.</div>
    </div>
    <div class="firma">
      <div class="linea">EL CLIENTE<br/>${cliente.representante || cliente.nombre}</div>
    </div>
  </div>
</body>
</html>`;
    openPrintWindow(head + body, true);
  };
  const imprimirControlEntrega = (contrato: Contrato) => {
    const cliente = clientes.find(c => c.id === contrato.clienteId);
    if (!cliente) {
      alert('No se encontró el cliente de este contrato.');
      return;
    }
    const tanques = getTanquesList(contrato, cliente);
    const cantidad = tanques.length;
    // Sumamos la capacidad total para el control de entrega
    const capacidadTotal = tanques.reduce((sum, t) => sum + (t.capacidad || 0), 0);
    const fmtFecha = (iso: string) => {
      if (!iso) return '';
      const d = new Date(iso + 'T00:00:00');
      return d.toLocaleDateString('es-DO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    };
    const css = `
      @page { size: landscape; margin: 1.5cm; }
      * { -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; box-sizing: border-box; }
      body { font-family: 'Arial', sans-serif; font-size: 12pt; color: #000; margin: 0; }
      .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; }
      .logo img { height: 140px; width: auto; }
      .doc-number { font-weight: bold; font-size: 14pt; margin-top: 20px; }
      h1 { text-align: center; font-size: 16pt; margin: -40px 0 30px 0; text-transform: uppercase; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 50px; text-align: center; }
      th, td { border: 1px solid #000; padding: 10px; font-size: 11pt; }
      th { background-color: #FFC000; font-weight: bold; }
      .signatures { margin-top: 60px; display: flex; flex-direction: column; gap: 40px; }
      .sig-line { display: flex; align-items: flex-end; gap: 10px; font-weight: bold; }
      .line { flex: 0 0 400px; border-bottom: 1px solid #000; }
      @media print { .no-print { display: none; } }
      .no-print { position: fixed; top: 14px; right: 14px; }
      .no-print button {
        background: #FF6B01; color: #fff; border: none; padding: 10px 18px;
        border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer;
      }
    `;
    const head = `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>Control de Entrega - ${contrato.numeroContrato}</title>
${styleTag(css)}
</head>`;
    const body = `
<body>
  <div class="no-print"><button onclick="window.print()">Imprimir / Guardar PDF</button></div>
  
  <div class="header">
    <div class="logo"><img src="${LOGO_DATA_URI}" alt="Junquito Gas" /></div>
    <div class="doc-number">No. ET-${contrato.numeroContrato.split('-').pop() || '0000'}</div>
  </div>

  <h1>CONTROL DE ENTREGA DE TANQUES GLP</h1>

  <table>
    <thead>
      <tr>
        <th>CLIENTE</th>
        <th>CONTACTO</th>
        <th>DIRECCIÓN</th>
        <th>TELÉFONO</th>
        <th>CANT. DE<br/>TANQUE</th>
        <th>CANT. DE<br/>GALONES</th>
        <th>FECHA DE<br/>ENTREGA</th>
        <th>DE DONDE<br/>PROVENÍA EL<br/>TANQUE</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>${cliente.nombre}</td>
        <td>${cliente.representante || '—'}</td>
        <td>${cliente.direccion || '—'}</td>
        <td>${cliente.telefono || '—'}</td>
        <td>${cantidad}</td>
        <td>${capacidadTotal}</td>
        <td>${fmtFecha(contrato.fechaInicio)}</td>
        <td>OFICINA PRINCIPAL</td>
      </tr>
    </tbody>
  </table>

  <div class="signatures">
    <div class="sig-line">
      <span>FIRMA DEL CLIENTE:</span>
      <div class="line"></div>
    </div>
    <div class="sig-line">
      <span>FIRMA DEL CHOFER:</span>
      <div class="line"></div>
    </div>
    <div class="sig-line">
      <span>AUTORIZADO POR:</span>
      <div class="line"></div>
    </div>
  </div>
</body>
</html>`;
    openPrintWindow(head + body, true);
  };
  const imprimirCartaRetiro = (contrato: Contrato) => {
    const cliente = clientes.find(c => c.id === contrato.clienteId);
    if (!cliente) {
      alert('No se encontró el cliente de este contrato.');
      return;
    }
    const ciudad = contrato.ciudad || 'Santiago';
    const tanques = getTanquesList(contrato, cliente);
    const fmtFechaLarga = () => {
      const d = new Date();
      return d.toLocaleDateString('es-DO', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    };
    const fmtMoneda = (n: number) => new Intl.NumberFormat('es-DO', {
      minimumFractionDigits: 2
    }).format(n);
    const css = `
      @page { size: letter; margin: 2.5cm; }
      * { -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; box-sizing: border-box; }
      body { font-family: 'Arial', sans-serif; font-size: 12pt; color: #000; line-height: 1.5; }
      .logo { margin-bottom: 20px; }
      .logo img { height: 140px; width: auto; }
      .date { text-align: right; font-weight: bold; margin-bottom: 40px; }
      .client-info { margin-bottom: 40px; }
      .client-info div { margin-bottom: 5px; }
      .body-text { margin-bottom: 30px; text-align: justify; }
      .tank-list { margin-bottom: 40px; padding-left: 20px; }
      .tank-list li { margin-bottom: 10px; }
      .closing { margin-bottom: 60px; }
      .signatures { display: flex; flex-direction: column; gap: 40px; }
      .sig-line { display: flex; align-items: flex-end; gap: 10px; font-weight: bold; }
      .line { flex: 0 0 300px; border-bottom: 1px solid #000; }
      @media print { .no-print { display: none; } }
      .no-print { position: fixed; top: 14px; right: 14px; }
      .no-print button {
        background: #FF6B01; color: #fff; border: none; padding: 10px 18px;
        border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer;
      }
    `;
    let tankItems = '';
    tanques.forEach(t => {
      tankItems += `<li>Un cilindro con capacidad de <strong>(${t.capacidad || 0})</strong> galones valorados en RD$${fmtMoneda(t.valor || 0)} pesos, este tanque es propiedad de nuestra empresa. -------------------------------------------------</li>`;
    });
    const head = `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>Carta de Retiro - ${contrato.numeroContrato}</title>
${styleTag(css)}
</head>`;
    const body = `
<body>
  <div class="no-print"><button onclick="window.print()">Imprimir / Guardar PDF</button></div>
  
  <div class="logo"><img src="${LOGO_DATA_URI}" alt="Junquito Gas" /></div>
  
  <div class="date">${ciudad}, Rep. Dom. ${fmtFechaLarga()}</div>

  <div class="client-info">
    <div><strong>Atención:</strong></div>
    <div><strong>${cliente.nombre}</strong></div>
    <div><strong>Teléfono:</strong> ${cliente.telefono || '—'}</div>
    <div><strong>Dirección</strong> ${cliente.direccion || '—'}</div>
  </div>

  <div class="body-text">
    Debido a la culminación del contrato de venta de GLP la Empresa Junquito Gas, procede a retirar de sus instalaciones los siguientes tanques:
  </div>

  <ul class="tank-list">
    ${tankItems}
  </ul>

  <div class="closing">
    Gracias por utilizar nuestros servicios.
  </div>

  <div class="signatures">
    <div class="sig-line">
      <span>Firma del Cliente:</span>
      <div class="line"></div>
    </div>
    <div class="sig-line">
      <span>Fecha del Retiro:</span>
      <div class="line"></div>
    </div>
    <div class="sig-line">
      <span>Firma del Chofer:</span>
      <div class="line"></div>
    </div>
  </div>
</body>
</html>`;
    openPrintWindow(head + body, true);
  };
  const generatePDF = (contrato: Contrato) => {
    const cliente = clientes.find(c => c.id === contrato.clienteId);
    if (!cliente) {
      alert('No se encontró el cliente de este contrato.');
      return;
    }
    const es = estadoStyle(contrato.estado);
    const tanques = getTanquesList(contrato, cliente);
    const cantidad = tanques.length;
    const fmtFecha = (iso: string) => {
      if (!iso) return '—';
      const d = new Date(iso + 'T00:00:00');
      return d.toLocaleDateString('es-DO', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    };
    const fmtMoneda = (n: number) => new Intl.NumberFormat('es-DO', {
      minimumFractionDigits: 2
    }).format(n);
    const css = `
      @page { size: letter; margin: 0; }
      * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
      body { margin: 0; font-family: 'DM Sans', -apple-system, Helvetica, Arial, sans-serif; color: #1A1207; background: #fff; }
      .sheet { max-width: 850px; margin: 0 auto; }
      .hero { background: linear-gradient(135deg, #FFFCF8 0%, #FFF3E6 100%); color: #1A1207; padding: 28px 44px 24px; position: relative; overflow: hidden; border-bottom: 4px solid #FF6B01; }
      .hero::before { content: ''; position: absolute; top: -90px; right: -70px; width: 280px; height: 280px; border-radius: 50%; background: radial-gradient(circle, rgba(255,107,1,.14) 0%, transparent 70%); }
      .hero::after { content: ''; position: absolute; bottom: -100px; left: -40px; width: 220px; height: 220px; border-radius: 50%; background: radial-gradient(circle, rgba(255,107,1,.08) 0%, transparent 70%); }
      .hero-top { display: flex; align-items: center; justify-content: space-between; position: relative; }
      .brand { display: flex; align-items: center; gap: 18px; }
      .brand img { height: 84px; width: auto; filter: drop-shadow(0 2px 6px rgba(0,0,0,.08)); }
      .brand-name { font-family: 'Space Grotesk', sans-serif; font-weight: 800; font-size: 20px; color: #1A1207; }
      .brand-sub { font-size: 10.5px; letter-spacing: .12em; text-transform: uppercase; color: rgba(26,18,7,.5); margin-top: 2px; }
      .doc-tag { font-size: 10px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: #FF6B01; text-align: right; }
      h1 { font-family: 'Space Grotesk', sans-serif; font-size: 24px; font-weight: 800; margin: 20px 0 4px; position: relative; color: #1A1207; }
      .contract-no { font-size: 13px; color: rgba(26,18,7,.6); position: relative; }
      .badge { display: inline-block; margin-top: 12px; padding: 5px 14px; border-radius: 99px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; background: ${es.soft}; color: ${es.color}; position: relative; }
      .body { padding: 30px 44px 0; }
      table.detalle { width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 22px; }
      table.detalle td { padding: 10px 4px; border-bottom: 1px solid rgba(26,18,7,.08); }
      table.detalle tr.seccion td { padding: 16px 4px 6px; border-bottom: 2px solid #FF6B01; font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: #FF6B01; }
      .celda-label { color: rgba(26,18,7,.55); width: 42%; }
      .celda-valor { font-weight: 700; text-align: right; }
      .total-card { background: linear-gradient(135deg, #FF8C00 0%, #FF5A00 100%); color: #fff; border-radius: 14px; padding: 20px 24px; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 10px 28px rgba(255,107,1,.28); margin-bottom: 22px; }
      .total-card .label { font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; opacity: .85; }
      .total-card .value { font-family: 'Space Grotesk', sans-serif; font-size: 27px; font-weight: 800; }
      .condiciones { border: 1px solid rgba(26,18,7,.12); border-radius: 12px; padding: 16px 20px; font-size: 13px; line-height: 1.6; color: rgba(26,18,7,.8); margin-bottom: 26px; }
      .condiciones h3 { font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: #FF6B01; margin: 0 0 10px; }
      footer { padding: 18px 44px; border-top: 1px solid rgba(26,18,7,.1); font-size: 11px; color: rgba(26,18,7,.5); display: flex; justify-content: space-between; }
      .no-print { position: fixed; top: 14px; right: 14px; }
      .no-print button { background: #FF6B01; color: #fff; border: none; padding: 10px 18px; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: sans-serif; box-shadow: 0 6px 18px rgba(255,107,1,.35); }
      @media print { .no-print { display: none; } }
    `;
    const seccion = (titulo: string) => `<tr class="seccion"><td colspan="2">${titulo}</td></tr>`;
    const fila = (label: string, value: string) => `<tr><td class="celda-label">${label}</td><td class="celda-valor">${value || '—'}</td></tr>`;
    let tankRows = '';
    tanques.forEach((t, i) => {
      tankRows += fila(`Cilindro ${i + 1} - Capacidad`, `${t.capacidad || '—'} galones`);
      tankRows += fila(`Cilindro ${i + 1} - Valor`, t.valor ? `RD$ ${fmtMoneda(t.valor)}` : '—');
    });
    const head = `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>Resumen ${contrato.numeroContrato}</title>
${styleTag(css)}
</head>`;
    const body = `
<body>
  <div class="no-print"><button onclick="window.print()">Imprimir / Guardar PDF</button></div>
  <div class="sheet">
    <div class="hero">
      <div class="hero-top">
        <div class="brand">
          <img src="${LOGO_DATA_URI}" alt="Junquito Gas" />
          <div>
            <div class="brand-name">Junquito Gas, SRL.</div>
            <div class="brand-sub">Gestión comercial · GLP industrial</div>
          </div>
        </div>
        <div class="doc-tag">Resumen de contrato</div>
      </div>
      <h1>Contrato ${contrato.numeroContrato}</h1>
      <div class="contract-no">Cliente: ${cliente.nombre}</div>
      <span class="badge">${contrato.estado}</span>
    </div>

    <div class="body">
      <table class="detalle">
        ${seccion('Datos del cliente')}
        ${fila('Nombre / razón social', cliente.nombre)}
        ${fila('RNC', cliente.rnc || '—')}
        ${fila('Teléfono', cliente.telefono)}
        ${fila('Dirección', cliente.direccion)}
        ${fila('Representante', cliente.representante || '—')}
        ${fila('Cédula del representante', cliente.representanteCedula || '—')}

        ${seccion('Datos del contrato')}
        ${fila('Número de contrato', contrato.numeroContrato)}
        ${fila('Ciudad', contrato.ciudad || 'Santiago de los Caballeros')}
        ${fila('Fecha de inicio', fmtFecha(contrato.fechaInicio))}
        ${fila('Fecha de vencimiento', fmtFecha(contrato.fechaVencimiento))}
        ${fila('Estado', contrato.estado)}

        ${seccion(`Cilindro(s) instalado(s) - Total: ${cantidad}`)}
        ${tankRows}
      </table>

      <div class="total-card">
        <span class="label">Precio acordado</span>
        <span class="value">RD$ ${fmtMoneda(contrato.precioAcordado || 0)}</span>
      </div>

      ${contrato.condiciones ? `<div class="condiciones"><h3>Condiciones comerciales</h3>${contrato.condiciones}</div>` : ''}
    </div>

    <footer>
      <span>Junquito Gas, SRL. · RNC 1-30-16152-6 · 809-247-2104</span>
      <span>Generado el ${new Date().toLocaleDateString('es-DO')}</span>
    </footer>
  </div>
</body>
</html>`;
    openPrintWindow(head + body, false);
  };
  const handleEdit = (contrato: Contrato) => {
    // Asegurarnos de que el form use el array de tanques si existe, o lo construya de los campos legacy
    const cliente = clientes.find(c => c.id === contrato.clienteId);
    const tanques = getTanquesList(contrato, cliente || {} as Cliente);
    setFormData({
      ...contrato,
      tanques
    });
    setSelectedCliente(contrato.clienteId);
    setEditingId(contrato.id);
    setShowForm(true);
  };
  const filteredContratos = contratos.filter(c => {
    const matchSearch = c.numeroContrato.toLowerCase().includes(searchTerm.toLowerCase()) || c.clienteNombre.toLowerCase().includes(searchTerm.toLowerCase());
    const matchEstado = filterEstado === 'todos' || c.estado === filterEstado;
    return matchSearch && matchEstado;
  });

  // Paginado: un listado largo obliga a hacer scroll para llegar a lo
  // último y hace la página pesada de dibujar.
  const {
    page,
    setPage,
    totalPages,
    pageItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(filteredContratos, 10);
  const contratosVigentes = contratos.filter(c => c.estado === 'vigente').length;
  const contratosVencidos = contratos.filter(c => c.estado === 'vencido').length;
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: 20
  }} data-id="element-438" data-mp="2a0a55xc54lmm">
      
      {/* Header Stats */}
      <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: 14
    }} data-id="element-439" data-mp="257gy32t7917d">
        
        <StatCard label="Total contratos" value={String(contratos.length)} note="Registrados en el sistema" accent={T.info} data-id="element-440" data-mp="1cknxckft81xd" />
        
        <StatCard label="Vigentes" value={String(contratosVigentes)} note="Activos hoy" accent={T.ok} data-id="element-441" data-mp="3clkqudoahwv8" />
        
        <StatCard label="Vencidos" value={String(contratosVencidos)} note="Requieren renovación" accent={T.danger} data-id="element-442" data-mp="3hjm94wdw5s5n" />
        
      </div>

      {/* Controles */}
      <div style={{
      ...glassLight,
      padding: 16
    }} data-id="element-443" data-mp="3rbjn2awaay82">
        
        <div style={{
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        alignItems: 'center'
      }} data-id="element-444" data-mp="2tnrlz5u3vcv7">
          
          <div style={{
          flex: '1 1 240px',
          position: 'relative'
        }} data-id="element-445" data-mp="2yne1jlxep576">
            
            <Search size={16} style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: T.textMuted
          }} data-id="element-446" data-mp="33k4gu71dzwrj" />
            
            <input type="text" placeholder="Buscar por número o cliente..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{
            ...inputStyle,
            paddingLeft: 36
          }} data-id="element-447" data-mp="ixjcta8nm4yh" />
            
          </div>
          <select value={filterEstado} onChange={e => setFilterEstado(e.target.value as any)} style={{
          ...inputStyle,
          width: 'auto'
        }} data-id="element-448" data-mp="23d93h4fqqrf3">
            
            <option value="todos" data-id="element-449" data-mp="1tnxuriwrnorq">Todos</option>
            <option value="vigente" data-id="element-450" data-mp="1tn6av5cj7fha">Vigentes</option>
            <option value="vencido" data-id="element-451" data-mp="1oocu941y78cw">Vencidos</option>
            <option value="cancelado" data-id="element-452" data-mp="1opwdn3iwhziw">Cancelados</option>
          </select>
          <button onClick={() => {
          setShowForm(!showForm);
          setEditingId(null);
          setFormData({
            estado: 'vigente',
            precioAcordado: 0,
            ciudad: 'Santiago de los Caballeros',
            tanques: [{
              capacidad: 120,
              valor: 140000
            }]
          });
          setSelectedCliente('');
        }} style={{
          ...primaryButton,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6
        }} data-id="element-453" data-mp="1jogvpkc0s94q">
            
            <Plus size={15} data-id="element-454" data-mp="21pukug9giv1s" /> Nuevo Contrato
          </button>
        </div>
      </div>

      {/* Formulario */}
      <AnimatePresence data-id="element-455" data-mp="1sr98j2ukwc9g">
        {showForm && <motion.div ref={refForm} initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2
      }} style={{
        overflow: 'hidden'
      }} data-id="element-456" data-mp="1ss29fn6uw7jo">
          
            <div style={{
          ...glass,
          padding: 20
        }} data-id="element-457" data-mp="1nsxxiosjrvpn">
            
              <h3 style={{
            fontSize: 15,
            fontWeight: 800,
            marginBottom: 16,
            color: T.text
          }} data-id="element-458" data-mp="1a22lx4qnz0id">
              
                {editingId ? 'Editar Contrato' : 'Nuevo Contrato'}
              </h3>
              <form onSubmit={handleSubmit} data-id="element-459" data-mp="151omuc71j0wh">
                <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 14
            }} data-id="element-460" data-mp="150d8xjjvscrn">
                
                  <Field label="Número de contrato *" data-id="element-461" data-mp="gh1sh40fbsoi">
                    <input type="text" value={formData.numeroContrato || ''} onChange={e => setFormData({
                  ...formData,
                  numeroContrato: e.target.value
                })} style={inputStyle} placeholder="CTR-2024-001" data-id="element-462" data-mp="gimn0tmqnbmd" />
                  
                  </Field>
                  <Field label="Cliente *" data-id="element-463" data-mp="14cxrkmxeb3rv">
                    <select value={selectedCliente} onChange={e => setSelectedCliente(e.target.value)} style={inputStyle} data-id="element-464" data-mp="zfxi021c7iw0">
                    
                      <option value="" data-id="element-465" data-mp="1e40s9nmexgqm">Selecciona un cliente</option>
                      {clientes.map(c => <option key={c.id} value={c.id} data-id="element-466" data-mp="196s8jilm94n9">
                          {c.nombre} ({c.codigo})
                        </option>)}
                    </select>
                  </Field>
                  <Field label="Fecha de inicio *" data-id="element-467" data-mp="1j4og60fchhgm">
                    <input type="date" value={formData.fechaInicio || ''} onChange={e => setFormData({
                  ...formData,
                  fechaInicio: e.target.value
                })} style={inputStyle} data-id="element-468" data-mp="1q6v1sjq5cj3c" />
                  
                  </Field>
                  <Field label="Fecha de vencimiento *" data-id="element-469" data-mp="203g9xdxgqabe">
                    <input type="date" value={formData.fechaVencimiento || ''} onChange={e => setFormData({
                  ...formData,
                  fechaVencimiento: e.target.value
                })} style={inputStyle} data-id="element-470" data-mp="24w9dhyh73clw" />
                  
                  </Field>
                  <Field label="Precio acordado (RD$)" data-id="element-471" data-mp="2iviswvxku2ot">
                    <input type="number" step="0.01" value={formData.precioAcordado || 0} onChange={e => setFormData({
                  ...formData,
                  precioAcordado: parseFloat(e.target.value)
                })} style={inputStyle} placeholder="0.00" data-id="element-472" data-mp="2iwtsejjzooa4" />
                  
                  </Field>
                  <Field label="Ciudad del contrato" data-id="element-473" data-mp="tgufcrd92ep7">
                    <input type="text" value={formData.ciudad || 'Santiago de los Caballeros'} onChange={e => setFormData({
                  ...formData,
                  ciudad: e.target.value
                })} style={inputStyle} data-id="element-474" data-mp="36r3zg1wg51qt" />
                  
                  </Field>
                  <Field label="Estado" data-id="element-475" data-mp="2szpehpizaqh4">
                    <select value={formData.estado || 'vigente'} onChange={e => setFormData({
                  ...formData,
                  estado: e.target.value as any
                })} style={inputStyle} data-id="element-476" data-mp="2t0qp5sw31ayb">
                    
                      <option value="vigente" data-id="element-477" data-mp="3lma70tb6x8m9">Vigente</option>
                      <option value="vencido" data-id="element-478" data-mp="3gnqhkixe1ep5">Vencido</option>
                      <option value="cancelado" data-id="element-479" data-mp="3bq72jgsv8xw5">Cancelado</option>
                    </select>
                  </Field>

                  {/* Sección dinámica de tanques */}
                  <div style={{
                gridColumn: '1 / -1',
                background: T.surfaceSubtle,
                padding: 16,
                borderRadius: 12,
                border: `1px solid ${T.border}`
              }} data-id="element-480" data-mp="257idgd2b5vbd">
                  
                    <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 12
                }} data-id="element-481" data-mp="97i14pu7bv6s">
                    
                      <h4 style={{
                    margin: 0,
                    fontSize: 13,
                    fontWeight: 700,
                    color: T.text
                  }} data-id="element-482" data-mp="3vfrka4div2o8">
                      
                        Cilindros Instalados
                      </h4>
                      <button type="button" onClick={handleAddTanque} style={{
                    ...ghostButton,
                    padding: '4px 10px',
                    fontSize: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4
                  }} data-id="element-483" data-mp="d68x2tmxl6sb">
                      
                        <Plus size={12} data-id="element-484" data-mp="2vdl7zvf4oo3h" /> Agregar cilindro
                      </button>
                    </div>

                    <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10
                }} data-id="element-485" data-mp="2qe073fgee5o4">
                    
                      {(formData.tanques || []).map((tanque, index) => <div key={index} style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'flex-end'
                  }} data-id="element-486" data-mp="305mnrrbi8wy9" data-mpi={index}>
                      
                          <div style={{
                      flex: 1
                    }} data-id="element-487" data-mp="27ia1zjd64t60">
                        
                            <Field label={`Capacidad (galones) #${index + 1}`} data-id="element-488" data-mp="2hed2ylwaalrd">
                              <input type="number" value={tanque.capacidad || ''} onChange={e => handleTanqueChange(index, 'capacidad', parseInt(e.target.value) || 0)} style={inputStyle} placeholder="120 / 250 / 500" data-id="element-489" data-mp="2hgg2bwjbtr8r" />
                          
                            </Field>
                          </div>
                          <div style={{
                      flex: 1
                    }} data-id="element-490" data-mp="c3d6kvzbz0uk">
                        
                            <Field label={`Valor (RD$) #${index + 1}`} data-id="element-491" data-mp="lv9gr5eoyhf5">
                              <input type="number" step="0.01" value={tanque.valor || ''} onChange={e => handleTanqueChange(index, 'valor', parseFloat(e.target.value) || 0)} style={inputStyle} placeholder="140000.00" data-id="element-492" data-mp="lxdj3iwxz8db" />
                          
                            </Field>
                          </div>
                          {(formData.tanques || []).length > 1 && <button type="button" onClick={() => handleRemoveTanque(index)} style={{
                      ...iconBtn,
                      color: T.danger,
                      marginBottom: 2
                    }} title="Eliminar cilindro" data-id="element-493" data-mp="362sz3z1izmh">
                        
                              <Trash2 size={16} data-id="element-494" data-mp="19lwrdyfzrl5e" />
                            </button>}
                        </div>)}
                    </div>
                  </div>

                  <div style={{
                gridColumn: '1 / -1'
              }} data-id="element-495" data-mp="2rl33g1jk2y9q">
                  
                    <Field label="Condiciones comerciales" data-id="element-496" data-mp="2ii2028m2rl9r">
                      <textarea value={formData.condiciones || ''} onChange={e => setFormData({
                    ...formData,
                    condiciones: e.target.value
                  })} style={{
                    ...inputStyle,
                    resize: 'vertical'
                  }} rows={4} placeholder="Especifica las condiciones del contrato..." data-id="element-497" data-mp="2ijmu7x4h9273" />
                    
                    </Field>
                  </div>
                </div>
                <div style={{
              display: 'flex',
              gap: 8,
              marginTop: 16
            }} data-id="element-498" data-mp="36f7nvq40es73">
                
                  <button type="submit" style={primaryButton} data-id="element-499" data-mp="3u4af9pog3zes">
                    {editingId ? 'Actualizar' : 'Crear'} contrato
                  </button>
                  <button type="button" onClick={() => {
                setShowForm(false);
                setEditingId(null);
              }} style={ghostButton} data-id="element-500" data-mp="cn67wduyvcq2">
                  
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Lista de Contratos */}
      <div style={{
      ...glass,
      padding: 20
    }} data-id="element-501" data-mp="3pt69wjoijsl5">
        
        <h3 style={{
        fontSize: 15,
        fontWeight: 800,
        marginBottom: 16,
        color: T.text
      }} data-id="element-502" data-mp="3pvj17ng5vfav">
          
          Contratos ({filteredContratos.length})
        </h3>
        {filteredContratos.length === 0 ? <EmptyState icon={<FileText size={20} data-id="element-504" data-mp="3b5bid6m8ru9n" />} title="No hay contratos" hint="Crea un contrato nuevo o ajusta los filtros de búsqueda." data-id="element-503" data-mp="3b520657furei" /> : <div style={{
        overflowX: 'auto'
      }} data-id="element-505" data-mp="3645nb253zuwy">
          
            <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 13
        }} data-id="element-506" data-mp="323c0kc3ekm5n">
            
              <thead data-id="element-507" data-mp="1hljjvxv5y6kb">
                <tr data-id="element-508" data-mp="1ho5p3qul09zd">
                  {['Nº Contrato', 'Cliente', 'Inicio', 'Vencimiento', 'Precio (RD$)', 'Estado', 'Acciones'].map((h, i) => <th key={h} style={{
                textAlign: i === 4 ? 'right' : i === 6 ? 'center' : 'left',
                padding: '0 10px 10px',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: T.textMuted,
                borderBottom: `1px solid ${T.border}`
              }} data-id="element-509" data-mp="1qlzlt2w9d5n2" data-mpi={i}>
                  
                      {h}
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-510" data-mp="1cnrqezevece0">
                {pageItems.map(contrato => {
              const es = estadoStyle(contrato.estado);
              return <tr key={contrato.id} data-id="element-511" data-mp="f72katiw0nyt">
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  fontFamily: 'monospace',
                  color: T.info,
                  fontWeight: 700
                }} data-id="element-512" data-mp="k5vwmimfu8xe">
                      
                        {contrato.numeroContrato}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  fontWeight: 600,
                  color: T.text
                }} data-id="element-513" data-mp="2vnhc4d3mwblp">
                      
                        {contrato.clienteNombre}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  color: T.textSecondary
                }} data-id="element-514" data-mp="3eka2o1lf2ycc">
                      
                        {contrato.fechaInicio}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  color: T.textSecondary
                }} data-id="element-515" data-mp="1k82d73rv79ny">
                      
                        {contrato.fechaVencimiento}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  textAlign: 'right',
                  fontWeight: 700,
                  color: T.text
                }} data-id="element-516" data-mp="3vvfchcjhez8s">
                      
                        {contrato.precioAcordado.toFixed(2)}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`
                }} data-id="element-517" data-mp="3d3we5j0ek9d3">
                      
                        <Badge label={contrato.estado} color={es.color} soft={es.soft} data-id="element-518" data-mp="3345gx9kgjg9b" />
                      
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  textAlign: 'center'
                }} data-id="element-519" data-mp="xcqiwzqtln9l">
                      
                        <div style={{
                    display: 'inline-flex',
                    gap: 6,
                    justifyContent: 'center'
                  }} data-id="element-520" data-mp="2a8eeucc76ajy">
                        
                          <button onClick={() => imprimirContrato(contrato)} title="Imprimir contrato" style={{
                      ...iconBtn,
                      color: '#8B5CF6'
                    }} data-id="element-521" data-mp="21bnjk25gyzqf">
                          
                            <Printer size={14} data-id="element-522" data-mp="2oz53jbihstiu" />
                          </button>
                          <button onClick={() => generatePDF(contrato)} title="Ver resumen en PDF" style={{
                      ...iconBtn,
                      color: T.ok
                    }} data-id="element-523" data-mp="2k8f1a75d56sd">
                          
                            <Download size={14} data-id="element-524" data-mp="3cq12csld8ozl" />
                          </button>
                          <button onClick={() => imprimirControlEntrega(contrato)} title="Imprimir Control de Entrega" style={{
                      ...iconBtn,
                      color: '#FF8C00'
                    }} data-id="element-525" data-mp="37xpvf5s7k2xj">
                          
                            <Truck size={14} data-id="element-526" data-mp="3kal4zrpoyd1i" />
                          </button>
                          <button onClick={() => imprimirCartaRetiro(contrato)} title="Imprimir Carta de Retiro" style={{
                      ...iconBtn,
                      color: '#38BDF8'
                    }} data-id="element-527" data-mp="3p60i2hfvk4wv">
                          
                            <ArrowDownToLine size={14} data-id="element-528" data-mp="71p3ht4nf0fv" />
                          </button>
                          <button onClick={() => handleEdit(contrato)} title="Editar" style={{
                      ...iconBtn,
                      color: T.info
                    }} data-id="element-529" data-mp="bsoyuty4dag9">
                          
                            <Edit2 size={14} data-id="element-530" data-mp="ktvjnf7sx7fb" />
                          </button>
                          <button onClick={() => onDeleteContrato(contrato.id)} title="Eliminar" style={{
                      ...iconBtn,
                      color: T.danger
                    }} data-id="element-531" data-mp="psga4mx94cqk">
                          
                            <Trash2 size={14} data-id="element-532" data-mp="1653hy51ssmmh" />
                          </button>
                        </div>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>}
        {filteredContratos.length ? <div style={{
        marginTop: 16
      }} data-id="element-533" data-mp="1g0nd2p8jfha6">
            <Pagination page={page} onChange={setPage} totalPages={totalPages} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="contratos" data-id="element-534" data-mp="sdozq0fpspgh" />

          </div> : null}
      </div>
    </div>;
}